import React, { useState, useEffect } from 'react';
import { deleteSubmission, getSummaryDownloadUrl, markNotSubmitted, getSubmissionDetail, excludeFromAverage } from '../services/api';

const styles = {
  backBtn: { background: 'none', border: 'none', color: '#9C3D97', cursor: 'pointer', fontSize: '14px', marginBottom: '16px', padding: 0 },
  heading: { fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '8px' },
  subheading: { fontSize: '20px', fontWeight: '700', color: '#2b6cb0', marginBottom: '24px' },
  scoreHeader: {
    display: 'flex', gap: '24px', padding: '20px', background: '#fff', borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px', alignItems: 'center',
  },
  scoreBig: { fontSize: '36px', fontWeight: '800' },
  scoreLabel: { fontSize: '12px', color: '#718096', textTransform: 'uppercase' },
  sectionTitle: { fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '16px', marginTop: '24px' },
  card: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: '#fff', padding: '20px', borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '12px', cursor: 'pointer',
    transition: 'box-shadow 0.2s',
  },
  cardLeft: { flex: 1 },
  cardScenario: { fontSize: '16px', fontWeight: '600', color: '#1a202c', marginBottom: '4px' },
  cardMeta: { fontSize: '13px', color: '#718096' },
  cardScore: { fontSize: '28px', fontWeight: '700', textAlign: 'right' },
  cardRating: { fontSize: '13px', fontWeight: '600', textAlign: 'right', marginTop: '4px' },
  ratingBadge: { padding: '3px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '700' },
  pendingCard: { opacity: 0.6 },
  reviewBtn: { padding: '8px 16px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginLeft: '16px' },
  deleteBtn: { padding: '8px 16px', background: '#fff', color: '#c53030', border: '1px solid #feb2b2', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginLeft: '8px' },
  error: { background: '#fed7d7', color: '#c53030', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' },
};

// Rating colors aligned to the Monday board (#12079750290). `bg`/`color` are
// for the badge; `text` is a readable-on-white variant for standalone numbers.
const ratingColors = {
  E: { bg: '#9078C0', color: '#ffffff', text: '#6b46c1' },
  EE: { bg: '#FDFD96', color: '#5c4d00', text: '#b7791f' },
  ME: { bg: '#32de84', color: '#0f5132', text: '#2f855a' },
  NI: { bg: '#FFB6C1', color: '#8a1c3b', text: '#b83280' },
  NS: { bg: '#e2e8f0', color: '#4a5568', text: '#4a5568' },
};

function RatingBadge({ rating }) {
  const colors = ratingColors[rating] || { bg: '#edf2f7', color: '#4a5568' };
  return (
    <span style={{ ...styles.ratingBadge, background: colors.bg, color: colors.color }}>
      {rating}
    </span>
  );
}

function formatAZ(isoString) {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    return d.toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) + ' AZ';
  } catch { return isoString.split('T')[0]; }
}

function sortNewestFirst(subs) {
  return [...subs].sort((a, b) => (b.submission_date || '').localeCompare(a.submission_date || ''));
}

export default function VaOverviewPage({ vaName, batch, submissions: initialSubmissions, gradingComplete: initialGradingComplete, missingGraded: initialMissing, onReview, onBack, lastReviewedId, tsName, cId, vaEmailProp }) {
  const [submissions, setSubmissions] = useState(sortNewestFirst(initialSubmissions));
  const [gradingComplete] = useState(initialGradingComplete);
  const [missingGraded, setMissingGraded] = useState(initialMissing || []);

  // The submissions prop is a snapshot taken when the trainee row was clicked
  // on Batch Results - so a score override applied in Submission Review
  // wasn't visible when navigating back here. Fix: on mount, point-read the
  // submission the user just reviewed (a single-row lookup by id -
  // milliseconds at any batch size; a per-VA server query was tried first
  // and rejected because non-key Table filters scan the whole partition,
  // ~27s) and patch its scores into the snapshot. The average recomputes
  // from state automatically.
  useEffect(() => {
    if (!lastReviewedId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const d = await getSubmissionDetail(lastReviewedId);
        if (cancelled || !d || d.error) return;
        setSubmissions((prev) => prev.map((s) => (s.submission_id === lastReviewedId ? {
          ...s,
          grand_total: d.grand_total !== undefined ? d.grand_total : s.grand_total,
          rating: d.rating !== undefined ? d.rating : s.rating,
          grading_status: d.grading_status || s.grading_status,
          override_flag: d.override_flag || s.override_flag,
          excluded_from_average: d.excluded_from_average !== undefined ? d.excluded_from_average : s.excluded_from_average,
        } : s)));
      } catch { /* keep snapshot */ }
    })();
    return () => { cancelled = true; };
  }, [lastReviewedId]);
  const [error, setError] = useState('');
  const [nsConfirm, setNsConfirm] = useState(null); // { scenario_id, scenario_name, day_name }
  const [nsSaving, setNsSaving] = useState(false);

  // Trainee email for the NS endpoint and the mistakes-CSV link. Prefer the
  // value threaded down from Batch Results (Monday #12017317679) and fall
  // back to scanning the submissions snapshot, which is how this worked
  // before and still covers a restored nav state from an older session.
  const vaEmail = vaEmailProp || (submissions.find(s => s.va_email)?.va_email) || '';

  async function handleDelete(submissionId, scenario, scenarioId, dayNumber, dayName) {
    if (!window.confirm(`Delete "${scenario}" case submission? This cannot be undone.`)) return;
    setError('');
    try {
      const result = await deleteSubmission(submissionId);
      if (result.error) { setError(result.error); return; }
      setSubmissions(prev => prev.filter(s => s.submission_id !== submissionId));
      // If the deleted row was a graded or NS case, put it back in the missing list
      // so the grader can mark it NS again if needed.
      if (scenarioId) {
        setMissingGraded(prev => {
          if (prev.some(m => m.scenario_id === scenarioId)) return prev;
          return [...prev, {
            scenario_id: scenarioId,
            scenario_name: scenario,
            day_number: dayNumber || 0,
            day_name: dayName || '',
          }];
        });
      }
    } catch { setError('Failed to delete submission.'); }
  }

  async function confirmMarkNs() {
    if (!nsConfirm || !vaEmail) return;
    setNsSaving(true);
    setError('');
    try {
      const result = await markNotSubmitted({
        batch,
        vaName,
        vaEmail,
        scenarioId: nsConfirm.scenario_id,
      });
      if (result.error) {
        setError(result.error);
        setNsSaving(false);
        return;
      }
      // Add the new NS row to the submissions list and remove it from missing list
      const newSub = {
        submission_id: result.submission_id,
        va_name: vaName,
        va_email: vaEmail,
        scenario: result.scenario_name || nsConfirm.scenario_name,
        scenario_id: nsConfirm.scenario_id,
        day_number: nsConfirm.day_number,
        day_name: nsConfirm.day_name,
        submission_date: result.ns_marked_at,
        grading_status: 'complete',
        no_submission: true,
        ns_marked_by: result.ns_marked_by,
        ns_marked_at: result.ns_marked_at,
        grand_total: 0,
        rating: 'NS',
        override_flag: 'No',
      };
      setSubmissions(prev => sortNewestFirst([...prev, newSub]));
      setMissingGraded(prev => prev.filter(m => m.scenario_id !== nsConfirm.scenario_id));
      setNsConfirm(null);
    } catch {
      setError('Failed to mark case as Not Submitted.');
    } finally {
      setNsSaving(false);
    }
  }

  async function handleToggleExclude(sub) {
    setError('');
    try {
      const result = await excludeFromAverage(sub.submission_id, !sub.excluded_from_average);
      if (result.error) { setError(result.error); return; }
      setSubmissions(prev => prev.map(s => s.submission_id === sub.submission_id
        ? { ...s, excluded_from_average: result.excluded_from_average, excluded_by: result.excluded_by }
        : s));
    } catch { setError('Failed to update average exclusion.'); }
  }

  // Only count completed submissions for average; grader-excluded rows keep
  // their score but don't count toward it (Monday #12204190005).
  const completed = submissions.filter(s => s.grading_status === 'complete');
  const counted = completed.filter(s => !s.excluded_from_average);
  const average = counted.length > 0
    ? counted.reduce((sum, s) => sum + (parseFloat(s.grand_total) || 0), 0) / counted.length
    : 0;

  // Derive rating from average
  let avgRating = '';
  if (average >= 95) avgRating = 'E';
  else if (average >= 86) avgRating = 'EE';
  else if (average >= 75) avgRating = 'ME';
  else if (average > 0) avgRating = 'NI';

  return (
    <div id="va-overview-top">
      <button style={styles.backBtn} onClick={onBack}>&larr; Back to All Trainees</button>
      <h1 style={styles.heading}>VA SOAP Note Evaluation Overview</h1>
      <p style={styles.subheading}>
        {vaName} — Batch {batch}
        {tsName && (
          <span style={{ marginLeft: '12px', padding: '4px 12px', borderRadius: '4px', fontSize: '13px', fontWeight: '600', background: '#e9d8fd', color: '#6b46c1' }}>TS: {tsName}</span>
        )}
        {gradingComplete && (
          <span style={{ marginLeft: '12px', padding: '4px 12px', borderRadius: '4px', fontSize: '13px', fontWeight: '700', background: '#c6f6d5', color: '#276749' }}>GRADING COMPLETE</span>
        )}
      </p>
      {error && <div style={styles.error}>{error}</div>}

      {/* Average Score Header */}
      <div style={styles.scoreHeader}>
        <div>
          <div style={styles.scoreLabel}>Average Score</div>
          <div style={{ ...styles.scoreBig, color: (ratingColors[avgRating] || {}).text || '#4a5568' }}>
            {average > 0 ? average.toFixed(2) + '%' : '—'}
          </div>
          {completed.length > counted.length && (
            <div style={{ fontSize: '11px', color: '#975a16', fontWeight: '600' }}>
              {completed.length - counted.length} graded case{completed.length - counted.length !== 1 ? 's' : ''} excluded
            </div>
          )}
        </div>
        <div>
          <div style={styles.scoreLabel}>Overall Rating</div>
          <div style={{ ...styles.scoreBig, color: (ratingColors[avgRating] || {}).text || '#4a5568' }}>
            {avgRating || '—'}
          </div>
        </div>
        <div>
          <div style={styles.scoreLabel}>Submissions</div>
          <div style={{ fontSize: '20px', fontWeight: '700' }}>{submissions.length} / 75</div>
        </div>
        <div>
          <div style={styles.scoreLabel}>Graded</div>
          <div style={{ fontSize: '20px', fontWeight: '700' }}>{completed.length}</div>
        </div>
        <div>
          <a
            href={getSummaryDownloadUrl(batch, vaName, cId, vaEmail)}
            style={{
              display: 'inline-block', padding: '10px 20px', background: '#9C3D97',
              color: '#fff', borderRadius: '6px', fontSize: '14px', fontWeight: '600',
              textDecoration: 'none', cursor: 'pointer',
            }}
          >
            Extract Mistakes to CSV
          </a>
        </div>
      </div>

      {/* Missing Required Submissions - graded cases with no result row yet */}
      {missingGraded.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h2 style={styles.sectionTitle}>Missing Required Submissions</h2>
          <div style={{ background: '#fffaf0', border: '1px solid #fbd38d', borderRadius: '8px', padding: '16px' }}>
            <div style={{ fontSize: '13px', color: '#744210', marginBottom: '12px' }}>
              These graded cases have no submission from this trainee. If the trainee does not submit them, mark each as <strong>Not Submitted</strong> to record a 0 score. This is permanent - reverse it only by deleting the NS record through the normal delete flow.
            </div>
            {(() => {
              const byDay = {};
              for (const m of missingGraded) {
                const dn = m.day_number || 0;
                if (!byDay[dn]) byDay[dn] = { day_name: m.day_name || (dn > 0 ? `Day ${dn}` : 'Unassigned'), items: [] };
                byDay[dn].items.push(m);
              }
              const dayKeys = Object.keys(byDay).sort((a, b) => Number(a) - Number(b));
              return dayKeys.map(dn => (
                <div key={dn} style={{ marginTop: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>
                    {byDay[dn].day_name}
                  </div>
                  {byDay[dn].items.map(m => (
                    <div key={m.scenario_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fff', borderRadius: '6px', marginBottom: '6px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: '500' }}>
                        {m.scenario_name || m.scenario_id}
                      </div>
                      <button
                        style={{ padding: '6px 14px', background: '#c53030', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                        onClick={() => setNsConfirm(m)}
                      >
                        Mark as Not Submitted
                      </button>
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* NS confirmation modal */}
      {nsConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', maxWidth: '480px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1a202c', marginBottom: '12px' }}>Mark as Not Submitted?</h3>
            <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: '#742a2a', fontWeight: '600', marginBottom: '4px' }}>This is permanent.</div>
              <div style={{ fontSize: '13px', color: '#742a2a' }}>
                A zero-score record will be created for <strong>{vaName}</strong> on case <strong>{nsConfirm.scenario_name}</strong> ({nsConfirm.day_name}). This records a 0% score with an NS rating and counts toward the trainee's average.
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#4a5568', marginBottom: '20px' }}>
              To reverse, you will need to delete the NS record through the normal delete flow. After deletion, the trainee would be able to submit this case normally if the suspense date still allows it.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                style={{ padding: '8px 16px', background: '#edf2f7', color: '#4a5568', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                onClick={() => setNsConfirm(null)}
                disabled={nsSaving}
              >
                Cancel
              </button>
              <button
                style={{ padding: '8px 16px', background: '#c53030', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: nsSaving ? 'not-allowed' : 'pointer', opacity: nsSaving ? 0.7 : 1 }}
                onClick={confirmMarkNs}
                disabled={nsSaving}
              >
                {nsSaving ? 'Saving...' : 'Yes, mark as Not Submitted'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky "Go to day" navigation (Monday #12594640467 Christian Medina
          + #12917267559 Dominique Rosales): jump straight to a day's charts
          instead of scrolling through up to 75 cards. Sticks to the top of
          the scroll area from here down; Top returns to the page header. */}
      {(() => {
        const dayNames = {};
        for (const s of submissions) {
          const dn = s.day_number || 0;
          if (!(dn in dayNames)) dayNames[dn] = s.day_name || (dn > 0 ? `Day ${dn}` : 'Unassigned');
        }
        const dayKeys = Object.keys(dayNames).sort((a, b) => Number(a) - Number(b));
        if (!submissions.length) return null;
        const showDayChips = dayKeys.length >= 2;
        const chip = { padding: '4px 12px', background: '#faf5ff', color: '#9C3D97', border: '1px solid #e9d8fd', borderRadius: '999px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' };
        const goTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
        return (
          <div style={{ position: 'sticky', top: 0, zIndex: 40, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', borderRadius: '8px', padding: '10px 16px', margin: '16px 0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {/* Back-nav in the floating bar (Monday #12960494349, Dominique
                Rosales): same handler as the page-top back button, so the
                return trip is available from any scroll depth. The bar now
                renders whenever there are submissions; the day chips still
                need at least two day groups to be worth showing. */}
            <button style={{ padding: '4px 12px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '999px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }} onClick={onBack}>&larr; Back to All Trainees</button>
            {showDayChips && (
              <>
                <span style={{ fontSize: '12px', color: '#718096', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Go to:</span>
                {dayKeys.map((dn) => (
                  <button key={dn} style={chip} onClick={() => goTo(`va-day-${dn}`)}>{dayNames[dn]}</button>
                ))}
                <button style={chip} onClick={() => goTo('va-overview-top')}>Top &uarr;</button>
              </>
            )}
          </div>
        );
      })()}

      {/* Individual Submission Cards - grouped by day */}
      <h2 style={styles.sectionTitle}>Individual Submissions</h2>
      {(() => {
        // Group by day, maintaining chronological numbering
        const sorted = [...submissions].sort((a, b) => (a.submission_date || '').localeCompare(b.submission_date || ''));
        const numbered = sorted.map((sub, i) => ({ sub, num: i + 1 }));
        // Group by day_number
        const dayGroups = {};
        for (const item of numbered) {
          const dn = item.sub.day_number || 0;
          if (!dayGroups[dn]) dayGroups[dn] = { day_name: item.sub.day_name || (dn > 0 ? `Day ${dn}` : 'Unassigned'), items: [] };
          dayGroups[dn].items.push(item);
        }
        const sortedDays = Object.keys(dayGroups).sort((a, b) => Number(a) - Number(b));
        return sortedDays.map((dn) => {
          const group = dayGroups[dn];
          const dayCompleted = group.items.filter(it => it.sub.grading_status === 'complete').length;
          const dayGraded = group.items.filter(it => it.sub.grading_status !== 'archived').length;
          return (
            <div key={dn} id={`va-day-${dn}`} style={{ scrollMarginTop: '64px' }}>
              <div style={{ padding: '8px 14px', background: '#f7fafc', borderRadius: '6px', marginBottom: '8px', marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#2d3748' }}>{group.day_name}</span>
                <span style={{ fontSize: '12px', color: '#718096' }}>
                  {group.items.length} submission{group.items.length !== 1 ? 's' : ''}
                  {dayGraded > 0 ? ` - ${dayCompleted} graded` : ' - practice'}
                </span>
              </div>
              {group.items.reverse().map(({ sub, num }) => {
        const isComplete = sub.grading_status === 'complete';
        const isNs = !!sub.no_submission;
        const subColors = ratingColors[isNs ? 'NS' : sub.rating] || {};
        const cardStyle = isNs
          ? { ...styles.card, background: '#f7fafc', border: '1px dashed #cbd5e0' }
          : { ...styles.card, ...(isComplete ? {} : styles.pendingCard) };
        return (
          <div
            key={sub.submission_id}
            style={cardStyle}
            onClick={() => isComplete && onReview(sub.submission_id)}
            onMouseEnter={(e) => { if (isComplete) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'; }}
          >
            <div style={styles.cardLeft}>
              <div style={styles.cardScenario}>
                SOAP Note {num}: {sub.scenario}
              </div>
              <div style={styles.cardMeta}>
                {isNs
                  ? `Not Submitted - marked by ${sub.ns_marked_by || 'admin'} on ${formatAZ(sub.ns_marked_at || sub.submission_date)}`
                  : <>Submitted {formatAZ(sub.submission_date)}{sub.override_flag === 'Yes' && ' · Override applied'}</>
                }
              </div>
              {sub.excluded_from_average && (
                <div style={{ marginTop: '6px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', background: '#fefcbf', color: '#975a16' }}>
                    EXCLUDED FROM AVERAGE{sub.excluded_by ? ` (by ${sub.excluded_by})` : ''}
                  </span>
                </div>
              )}
            </div>
            <div>
              {isComplete ? (
                <>
                  {/* `text` is the readable-on-white variant; `color` is the
                      badge foreground (white for E - which made E scores
                      invisible on the white card). */}
                  <div style={{ ...styles.cardScore, color: subColors.text || '#4a5568' }}>
                    {isNs ? '0%' : `${sub.grand_total}%`}
                  </div>
                  <div style={styles.cardRating}>
                    <RatingBadge rating={isNs ? 'NS' : sub.rating} />
                  </div>
                </>
              ) : sub.grading_status === 'failed_permanent' ? (
                <div style={{ ...styles.cardScore, color: '#c53030', fontSize: '16px' }}>FAILED</div>
              ) : sub.grading_status === 'archived' ? (
                <div style={{ ...styles.cardScore, color: '#718096', fontSize: '14px' }}>Archived</div>
              ) : (
                <div style={{ ...styles.cardScore, color: '#975a16', fontSize: '16px' }}>Grading...</div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginLeft: '16px' }}>
              {isComplete && !isNs && (
                <button style={styles.reviewBtn} onClick={(e) => { e.stopPropagation(); onReview(sub.submission_id); }}>
                  Review
                </button>
              )}
              {isComplete && !isNs && (
                <button
                  style={{ ...styles.deleteBtn, color: '#975a16', border: '1px solid #f6e05e', marginLeft: '16px' }}
                  title={sub.excluded_from_average
                    ? 'Count this submission in the average again'
                    : "Keep the score visible but leave it out of this trainee's average"}
                  onClick={(e) => { e.stopPropagation(); handleToggleExclude(sub); }}
                >
                  {sub.excluded_from_average ? 'Include in Avg' : 'Exclude from Avg'}
                </button>
              )}
              <button style={styles.deleteBtn} onClick={(e) => { e.stopPropagation(); handleDelete(sub.submission_id, sub.scenario, sub.scenario_id, sub.day_number, sub.day_name); }}>
                Delete
              </button>
            </div>
          </div>
        );
              })}
            </div>
          );
        });
      })()}

      {submissions.length === 0 && (
        <div style={{ color: '#a0aec0', textAlign: 'center', padding: '40px', fontSize: '14px' }}>
          No submissions found for this VA.
        </div>
      )}
    </div>
  );
}
